import { useFormik } from 'formik';
import { Autocomplete, Box, Button, Grid, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { camelCase, get as _get, isEmpty, replace as _replace } from 'lodash';
import { useEffect } from 'react';
import { tokens, TranslatedToken, useTranslation } from '@shared/utils/i18n';
import { formatPhone } from '@shared/utils/helpers/formatString';
import { AbonentSchema } from '@shared/validation/osi/abonents';
import { AreaType, AreaTypeCodes } from '@shared/types/dictionaries';
import { Abonent } from '@shared/types/osi/abonents';
import { MaskInput } from '@shared/common/MaskInput';
import api from '@app/api';
import { notifications } from '@shared/utils/notifications';
import { observer } from 'mobx-react-lite';

enum OwnerCode {
  Owner,
  Arendator
}

type OwnerType = {
  code: OwnerCode;
  nameRu: string;
  nameKz: string;
};

const ownerTypes: OwnerType[] = [
  {
    code: OwnerCode.Owner,
    nameRu: 'Собственник',
    nameKz: 'меншік иесі'
  },
  {
    code: OwnerCode.Arendator,
    nameRu: 'Арендатор',
    nameKz: 'жалға алушы'
  }
];

interface Props {
  data?: any;
  onClose: any;
  totalAbonents: number;
  osiId: number | null;
  areaTypes: AreaType[];
  abonents?: Abonent[];  // Фаза 2: список абонентов для привязки паркинга/кладовки
}

export const OsiAddAbonentForm: React.FC<Props> = observer(({ data, onClose, totalAbonents, osiId, areaTypes, abonents = [] }) => {
  const { translateToken: tt, fieldWithPrefix: fwp, t } = useTranslation();

  const isCreate = !data;

  // Бэк (OSI.Core dev 34d6a1f): areaTypeCode === 'ARENDATOR' нельзя выбрать в общей форме абонента
  // и нельзя сменить тип у существующего арендатора. Также BASEMENT удалён как тип.
  const isArendator = data?.areaTypeCode === 'ARENDATOR';

  // Дефолтный areaTypeCode — первый ИЗ ДОПУСТИМЫХ (исключая ARENDATOR/BASEMENT)
  const selectableAreaTypes = (areaTypes || []).filter(
    (option: AreaType) => option.code !== 'ARENDATOR' && option.code !== 'BASEMENT'
  );
  const defaultAreaCode = selectableAreaTypes.length ? selectableAreaTypes[0]?.code : '';

  const initialModel = {
    // eslint-disable-next-line no-unsafe-optional-chaining
    flat: data?.flat || (totalAbonents ? `${totalAbonents + 1}` : '').toString(),
    name: data?.name || '',
    idn: data?.idn || '',
    phone: formatPhone(data?.phone) || '',
    areaTypeCode: data?.areaTypeCode || defaultAreaCode,
    floor: String(data?.floor || ''),
    square: String(data?.square || ''),
    effectiveSquare: data?.effectiveSquare || '',
    livingJur: String(data?.livingJur || ''),
    livingFact: String(data?.livingFact || ''),
    owner: ownerTypes.find((types) => types.nameRu === data?.owner) || '',
    id: data?.id,
    parentAbonentId: data?.parentAbonentId || null  // Фаза 2: привязка к родительскому помещению
  };

  const formik = useFormik({
    initialValues: initialModel,
    validationSchema: AbonentSchema,
    onSubmit: (values) => {
      console.log(values);
    }
  });

  const { values, getFieldProps, touched, setValues, errors, setTouched, setErrors, isSubmitting, setSubmitting } =
    formik;

  useEffect(() => {
    formik.validateForm().then((errors) => {
      Object.keys(errors).forEach((key) => {
        formik.setFieldTouched(key, true, true);
      });
    });
  }, []);

  const handleAbonentUpdate = async (data: any, setErrors: any, setSubmitting: any) => {
    const values = {
      phone: _replace(_replace(data.phone, '+7 7', '7'), new RegExp(' ', 'g'), ''),
      floor: data.floor ? parseInt(data.floor, 10) : 0,
      livingJur: data.livingJur ? parseInt(data.livingJur, 10) : 0,
      livingFact: data.livingFact ? parseInt(data.livingFact, 10) : 0,
      square: data.square ? parseFloat(data.square) : 0,
      effectiveSquare: data.effectiveSquare ? parseFloat(data.effectiveSquare) : 0,
      flat: data.flat,
      name: data.name,
      idn: data.idn,
      owner: data.owner.nameRu,
      areaTypeCode: data.areaTypeCode,
      // external удалён бэкендом (OSI.Core dev 34d6a1f) — не отправляем.
      osiId: osiId ?? '',
      parentAbonentId: data.parentAbonentId || null  // Фаза 2: привязка к родительскому помещению
    };

    try {
      if (isCreate) {
        await api.AbonentCreate(values);
      } else {
        await api.AbonentEdit(data.id, values);
      }

      notifications.success('Сохранено');
      onClose(true);
    } catch (err: any) {
      const errors = _get(err, ['response', 'data', 'errors']);
      if (isEmpty(errors)) {
        notifications.error(err.toString());
      } else {
        const formikErrors = {} as Record<any, any>;
        Object.keys(errors).forEach((key) => {
          formikErrors[camelCase(key)] = errors[key];
        });

        setErrors({ ...formikErrors });
      }
      setSubmitting(false);
    }
  };

  // PARKING/STORAGE могут иметь parent любого типа (квартира/нежилое/паркинг/кладовка/арендатор)
  // или быть корневыми (parent=NULL — самостоятельная парковка). Поле всегда опциональное.
  const parentAbonentOptions = abonents.filter((a) => a.id !== data?.id);
  const isChildAreaType = values.areaTypeCode === 'PARKING' || values.areaTypeCode === 'STORAGE';

  // Для отображения текущего значения у редактируемого ARENDATOR используем полный areaTypes,
  // т.к. в selectableAreaTypes ARENDATOR уже отфильтрован.
  const idx = areaTypes.findIndex((x: any) => x.code === values.areaTypeCode);
  const ownerIndex = ownerTypes.findIndex((x) => x.code === (values.owner as OwnerType)?.code);

  return (
    <>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={3} sm={2}>
          <TextField
            fullWidth
            label={tt(tokens.common.formFields.number.label)}
            inputProps={{ style: { textAlign: 'center' } }}
            {...getFieldProps('flat')}
            error={Boolean(touched.flat && errors.flat)}
            helperText={(touched.flat && errors.flat && tt(errors.flat.toString())) as string}
          />
        </Grid>
        <Grid item xs={9} sm={4}>
          <Autocomplete
            fullWidth
            disableClearable
            disabled={isArendator}
            getOptionLabel={(option) => fwp(option, 'name')}
            renderOption={(props, option) => <li {...props}>{fwp(option, 'name')}</li>}
            options={selectableAreaTypes}
            onChange={(event, value) => {
              const newCode = value?.code || '';
              // При смене типа на не-child (RES/NON_RES/ARENDATOR) — занулить parentAbonentId.
              // Бэк делает то же самое в AbonentSvc.UpdateAbonent (страховка для прямых API-клиентов).
              const isNewChild = newCode === 'PARKING' || newCode === 'STORAGE';
              setValues({
                ...values,
                areaTypeCode: newCode,
                parentAbonentId: isNewChild ? values.parentAbonentId : null
              });
            }}
            value={areaTypes[idx]}
            renderInput={(params) => (
              <TextField
                {...params}
                label={tt(tokens.common.formFields.flatType.label)}
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  autoComplete: 'new-password'
                }}
                onBlur={() => {
                  setTouched({
                    ...touched,
                    areaTypeCode: true
                  });
                }}
                error={Boolean(touched.areaTypeCode && errors.areaTypeCode)}
                helperText={
                  (touched.areaTypeCode && errors.areaTypeCode && tt(errors.areaTypeCode.toString())) as string
                }
                style={{ margin: 0 }}
              />
            )}
          />
        </Grid>
        {/* Привязка к родительскому помещению — опциональна для PARKING/STORAGE.
            Без выбора (parent=NULL) парковка/кладовка корневые: самостоятельный ЛС, отдельные счета. */}
        {isChildAreaType && (
          <Grid item xs={6} sm={3}>
            <Autocomplete
              fullWidth
              options={parentAbonentOptions}
              // Фаза 2: родителем может быть и арендатор — показываем его flatDisplay (единый номер от backend),
              // иначе сырой flat. Привязка идёт по option.id, поэтому меняется только отображение.
              getOptionLabel={(option) =>
                `${
                  option.areaTypeCode === AreaTypeCodes.ARENDATOR ? (option.flatDisplay ?? option.flat) : option.flat
                } ${option.name || ''}`
              }
              value={parentAbonentOptions.find((a) => a.id === values.parentAbonentId) || null}
              onChange={(event, newValue) => {
                setValues({
                  ...values,
                  parentAbonentId: newValue?.id || null
                });
                setTouched({ ...touched, parentAbonentId: true });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('abonents:parentAbonent')}
                  margin="normal"
                  style={{ margin: 0 }}
                  InputProps={{
                    ...params.InputProps,
                    autoComplete: 'new-password'
                  }}
                  onBlur={() => {
                    setTouched({ ...touched, parentAbonentId: true });
                  }}
                  error={Boolean(touched.parentAbonentId && errors.parentAbonentId)}
                  helperText={
                    (touched.parentAbonentId &&
                      errors.parentAbonentId &&
                      tt(errors.parentAbonentId.toString())) as string
                  }
                />
              )}
            />
          </Grid>
        )}
        <Grid item xs={6} sm={3}>
          <MaskInput
            label={tt(tokens.common.formFields.floor.label)}
            mask="##"
            align="right"
            error={Boolean(touched.floor && errors.floor)}
            helperText={(touched.floor && errors.floor && tt(errors.floor.toString())) as string}
            {...getFieldProps('floor')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MaskInput
            label={tt(tokens.common.formFields.square.label)}
            type="amount"
            align="right"
            maxLimit={9999.99}
            error={Boolean(touched.square && errors.square)}
            helperText={(touched.square && errors.square && tt(errors.square.toString())) as string}
            {...getFieldProps('square')}
            onChange={(e) => {
              setValues({ ...values, square: e.target.value });
            }}
            onBlur={() => {
              setTouched({ ...touched, square: true });
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={3}>
          <MaskInput
            label={tt(tokens.common.formFields.effectiveSquare.label)}
            type="amount"
            align="right"
            maxLimit={9999.99}
            error={Boolean(touched.effectiveSquare && errors.effectiveSquare)}
            helperText={
              (touched.effectiveSquare && errors.effectiveSquare && tt(errors.effectiveSquare.toString())) as string
            }
            {...getFieldProps('effectiveSquare')}
            onChange={(e) => {
              setValues({ ...values, effectiveSquare: e.target.value });
            }}
            onBlur={() => {
              setTouched({ ...touched, effectiveSquare: true });
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <MaskInput
            label={tt(tokens.common.formFields.iin.label)}
            mask="############"
            error={Boolean(touched.idn && errors.idn)}
            helperText={(touched.idn && errors.idn && tt(errors.idn.toString())) as string}
            {...getFieldProps('idn')}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <MaskInput
            mask="+7 7## ### ## ##"
            label={tt(tokens.common.formFields.phone.label)}
            {...getFieldProps('phone')}
            error={Boolean(touched.phone && errors.phone)}
            helperText={(touched.phone && errors.phone && tt(errors.phone.toString())) as string}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={tt(tokens.common.formFields.owner.label)}
            {...getFieldProps('name')}
            error={Boolean(touched.name && errors.name)}
            helperText={(touched.name && errors.name && tt(errors.name.toString())) as string}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Autocomplete
            fullWidth
            disableClearable
            getOptionLabel={(option) => fwp(option, 'name')}
            renderOption={(props, option) => <li {...props}>{fwp(option, 'name')}</li>}
            options={ownerTypes}
            onChange={(event, value) =>
              setValues({
                ...values,
                owner: value || ''
              })
            }
            value={ownerTypes[ownerIndex] || undefined}
            renderInput={(params) => (
              <TextField
                {...params}
                label={tt(tokens.common.formFields.owner.labelAlt)}
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  autoComplete: 'new-password'
                }}
                onBlur={() => {
                  setTouched({
                    ...touched,
                    owner: true
                  });
                }}
                error={Boolean(touched.owner && errors.owner)}
                helperText={(touched.owner && errors.owner && tt(errors.owner.toString())) as string}
                style={{ margin: 0 }}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MaskInput
            label={tt(tokens.common.formFields.livingFact.label)}
            mask="###"
            align="right"
            error={Boolean(touched.livingFact && errors.livingFact)}
            helperText={(touched.livingFact && errors.livingFact && tt(errors.livingFact.toString())) as string}
            {...getFieldProps('livingFact')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MaskInput
            label={tt(tokens.common.formFields.livingJur.label)}
            mask="###"
            align="right"
            error={Boolean(touched.livingJur && errors.livingJur)}
            helperText={(touched.livingJur && errors.livingJur && tt(errors.livingJur.toString())) as string}
            {...getFieldProps('livingJur')}
          />
        </Grid>
        <Box
          width="100%"
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Button
            onClick={() => {
              onClose(false);
            }}
            color="inherit"
            sx={{ mr: 1 }}
            size="large"
          >
            <TranslatedToken id={tokens.common.cancel} />
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!isEmpty(errors)}
            size="large"
            onClick={() => {
              handleAbonentUpdate(values, setErrors, setSubmitting);
            }}
          >
            <TranslatedToken id={tokens.common.save} />
          </LoadingButton>
        </Box>
      </Grid>
    </>
  );
});
